import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import SEO from '../components/seo/SEO';
import { breadcrumb } from '../components/seo/jsonLd';

const CATEGORIES = ['All', 'Hair', 'Color', 'Bridal', 'Makeup', 'Nails'];

const GALLERY = Array.from({ length: 18 }, (_, i) => ({
  id: i + 1,
  category: CATEGORIES[(i % (CATEGORIES.length - 1)) + 1],
  title: ['Balayage Transformation', 'Bridal Updo', 'Bold Color', 'Keratin Blowout', 'Nail Art', 'Smoky Eye'][i % 6],
  gradient: ['from-salon-primary/30 to-salon-secondary/20', 'from-salon-secondary/30 to-pink-200/40', 'from-purple-400/20 to-salon-primary/20',
    'from-green-200/30 to-teal-100/20', 'from-pink-200/30 to-purple-100/20', 'from-orange-200/30 to-red-100/20'][i % 6],
  emoji: ['💇', '👑', '🎨', '✨', '💅', '💄'][i % 6],
}));

export default function Gallery() {
  const [active, setActive] = useState('All');
  const [lightbox, setLightbox] = useState(null);

  const filtered = active === 'All' ? GALLERY : GALLERY.filter(g => g.category === active);

  const navigate = (dir) => {
    const idx = filtered.findIndex(g => g.id === lightbox.id);
    const next = (idx + dir + filtered.length) % filtered.length;
    setLightbox(filtered[next]);
  };

  return (
    <>
      <SEO
        title="Gallery — Real Salon Vora Transformations"
        description="Browse before-and-after transformations from Salon Vora. Hair colour, balayage, bridal makeup, nails and more — see why our clients love us."
        path="/gallery"
        jsonLd={breadcrumb([
          { name: 'Home', url: '/' },
          { name: 'Gallery', url: '/gallery' },
        ])}
      />
    <div className="min-h-screen bg-salon-cream">
      {/* Hero */}
      <section className="relative bg-hero-gradient py-32 text-center overflow-hidden">
        <div className="absolute inset-0 opacity-30"
          style={{ backgroundImage:'radial-gradient(circle at 70% 30%, #C9963A 0%, transparent 60%)' }} />
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-salon-primary/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-[400px] h-[400px] rounded-full bg-salon-secondary/15 blur-3xl pointer-events-none" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.span initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }}
            className="section-tag mb-4 inline-block">Our Work</motion.span>
          <motion.h1 initial={{ opacity:0, y:30 }} animate={{ opacity:1, y:0 }} transition={{ delay:.1 }}
            className="font-display text-4xl md:text-6xl text-white mb-6">
            Transformations That<br /><span className="text-salon-secondary">Speak for Themselves</span>
          </motion.h1>
        </div>
      </section>

      {/* Filter */}
      <section className="sticky top-16 z-30 bg-white border-b border-salon-cream-dark shadow-sm">
        <div className="container mx-auto px-4 py-4 flex gap-2 overflow-x-auto scrollbar-none">
          {CATEGORIES.map(cat => (
            <button key={cat} onClick={() => setActive(cat)}
              className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all
                ${active === cat ? 'bg-salon-primary text-white shadow-salon-md' : 'bg-salon-cream text-salon-muted hover:bg-salon-cream-dark'}`}>
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Masonry Grid */}
      <section className="py-16 container mx-auto px-4">
        <motion.div layout className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
          <AnimatePresence>
            {filtered.map((item) => (
              <motion.div key={item.id} layout
                initial={{ opacity:0, scale:.9 }} animate={{ opacity:1, scale:1 }} exit={{ opacity:0, scale:.9 }}
                className={`break-inside-avoid rounded-2xl overflow-hidden cursor-pointer group
                  bg-gradient-to-br ${item.gradient} ${item.id % 3 === 0 ? 'aspect-[3/4]' : 'aspect-square'}`}
                onClick={() => setLightbox(item)}>
                <div className="w-full h-full min-h-[200px] flex flex-col items-center justify-center
                  group-hover:bg-salon-dark/20 transition-colors relative">
                  <span className="text-6xl mb-3 group-hover:scale-110 transition-transform">{item.emoji}</span>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-salon-dark/60 to-transparent
                    p-4 translate-y-full group-hover:translate-y-0 transition-transform">
                    <p className="text-white font-medium text-sm">{item.title}</p>
                    <p className="text-white/60 text-xs">{item.category}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            className="fixed inset-0 z-50 bg-salon-dark/95 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}>
            <button onClick={() => setLightbox(null)}
              className="absolute top-6 right-6 text-white hover:text-salon-secondary text-2xl">
              <FiX />
            </button>
            <button onClick={(e) => { e.stopPropagation(); navigate(-1); }}
              className="absolute left-6 text-white hover:text-salon-secondary text-2xl p-3">
              <FiChevronLeft />
            </button>
            <button onClick={(e) => { e.stopPropagation(); navigate(1); }}
              className="absolute right-6 text-white hover:text-salon-secondary text-2xl p-3">
              <FiChevronRight />
            </button>
            <motion.div initial={{ scale:.9 }} animate={{ scale:1 }}
              className={`w-full max-w-lg aspect-square rounded-3xl bg-gradient-to-br ${lightbox.gradient}
                flex flex-col items-center justify-center`}
              onClick={(e) => e.stopPropagation()}>
              <span className="text-8xl mb-4">{lightbox.emoji}</span>
              <h3 className="font-display text-2xl text-salon-dark">{lightbox.title}</h3>
              <p className="text-salon-muted">{lightbox.category}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
    </>
  );
}
